/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-12
 */

// Add third-party dependencies.
import { createApi } from '@reduxjs/toolkit/query/react';

// Add local dependencies.
import { baseQueryWithReauth } from '../../api/baseQuery';
import type {
  DashboardOverviewResponse,
  DashboardActivityResponse,
  DashboardHealthResponse,
  DashboardActivityRequest,
} from '../../types/dashboard';

/**
 * Dashboard API slice for managing dashboard data.
 *
 * Provides endpoints for:
 * - Dashboard overview data (user summary, studios, quick actions, recent activity)
 * - Recent activity feed with pagination
 * - Dashboard health check
 */
export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Dashboard', 'DashboardActivity'],
  endpoints: builder => ({
    /**
     * Get dashboard overview data.
     *
     * Fetches comprehensive dashboard data including user summary,
     * studio memberships, quick actions, and recent activity.
     *
     * Features:
     * - 5-minute cache duration (matches backend caching)
     * - Automatic invalidation on user/studio changes
     * - Error handling with fallback data
     */
    getDashboardOverview: builder.query<DashboardOverviewResponse, void>({
      query: () => ({
        url: 'dashboard/overview/',
        method: 'GET',
      }),
      providesTags: ['Dashboard'],
      // Cache for 5 minutes to match backend caching
      keepUnusedDataFor: 300,
    }),

    /**
     * Get dashboard activity feed.
     *
     * Fetches recent activity data with optional pagination.
     * Can be called separately from overview for real-time updates.
     *
     * @param params - Request parameters including optional limit
     */
    getDashboardActivity: builder.query<
      DashboardActivityResponse,
      DashboardActivityRequest | void
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        if (params && params.limit) {
          searchParams.append('limit', params.limit.toString());
        }

        const queryString = searchParams.toString();
        return {
          url: `dashboard/activity/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['DashboardActivity'],
      // Cache for 1 minute for more frequent updates
      keepUnusedDataFor: 60,
    }),

    /**
     * Dashboard health check.
     *
     * Provides basic status information about the dashboard service
     * and user authentication status.
     */
    getDashboardHealth: builder.query<DashboardHealthResponse, void>({
      query: () => ({
        url: 'dashboard/health/',
        method: 'GET',
      }),
      // Don't cache health checks
      keepUnusedDataFor: 0,
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetDashboardOverviewQuery,
  useLazyGetDashboardOverviewQuery,
  useGetDashboardActivityQuery,
  useLazyGetDashboardActivityQuery,
  useGetDashboardHealthQuery,
  useLazyGetDashboardHealthQuery,
} = dashboardApi;

// Export the reducer
export default dashboardApi.reducer;
